import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Grid,
  Pagination,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getCategoriesByPriceRule,
  assignPriceRuleToCategories,
  removePriceRuleFromCategory,
} from "../../../../redux/actions/priceRuleActions";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import store from "../../../../redux/store/store";
import CloseIcon from "@mui/icons-material/Close";

const CategoryAssignmentModal = ({ open, onClose, priceRule }) => {
  const dispatch = useDispatch();
  const { categoriesByRule, categoriesLoading } = useSelector(
    (state) => state.priceRule
  );
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Kurala bağlı kategorileri getir
  useEffect(() => {
    if (open && priceRule) {
      dispatch(getCategoriesByPriceRule(priceRule.id, 1, ""));
    }
  }, [open, priceRule, dispatch]);

  // Eğer kategori yoksa veya atama yapılmak isteniyorsa, tüm kategorileri getir
  useEffect(() => {
    if (open) {
      fetchAllCategories();
    }
    // eslint-disable-next-line
  }, [open, page, searchTerm]);

  // Tüm kategorileri getir (atama için)
  const fetchAllCategories = async () => {
    setLoading(true);
    try {
      const userToken = store.getState()?.auth?.user?.accessToken?.token;
      const res = await fetch(
        `${
          import.meta.env.VITE_API_URL
        }/Category?page=${page}&name=${searchTerm}`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );
      const data = await res.json();
      setAllCategories(data.data?.data || []);
      setTotalPages(Math.ceil((data.data?.recordsTotal || 0) / 10));
    } catch (err) {
      setAllCategories([]);
      toast.error("Kategoriler yüklenirken bir hata oluştu.");
    }
    setLoading(false);
  };

  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Arama terimi değiştiğinde sayfayı 1'e sıfırla
  };

  // Checkbox ile kategori seçimi
  const handleCategoryCheck = (cat) => {
    if (selectedCategories.find((c) => c.id === cat.id)) {
      setSelectedCategories(selectedCategories.filter((c) => c.id !== cat.id));
    } else {
      setSelectedCategories([...selectedCategories, cat]);
    }
  };
  const handleRemoveCategory = (catId) => {
    setSelectedCategories(selectedCategories.filter((c) => c.id !== catId));
  };

  // Toplu atama işlemi
  const handleAssign = async () => {
    if (selectedCategories.length === 0) return;
    try {
      const userToken = store.getState()?.auth?.user?.accessToken?.token;
      const now = new Date().toISOString();
      const bodyArray = selectedCategories.map((cat) => ({
        id: cat.id,
        userId: cat.userId || 0,
        name: cat.name,
        vatRate: cat.vatRate || 0,
        priceRuleId: priceRule.id,
        createDate: cat.createDate || now,
        updateDate: now,
        ia: typeof cat.ia === "boolean" ? cat.ia : true,
      }));
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/Category/UpdateRange`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify(bodyArray),
        }
      );

      const responseData = await response.json();

      if (responseData.status) {
        toast.success(responseData.message || "Kategoriler başarıyla atandı");
        setSelectedCategories([]);
        dispatch(getCategoriesByPriceRule(priceRule.id, 1, ""));
      } else {
        toast.error(responseData.message || "Atama sırasında bir hata oluştu.");
      }
    } catch (err) {
      toast.error(
        "Atama işlemi sırasında bir hata oluştu. Lütfen internet bağlantınızı kontrol edin veya daha sonra tekrar deneyin."
      );
    }
  };

  // Kurala bağlı kategoriyi kaldır (priceRuleId=null)
  const handleRemoveAssignedCategory = async (catId) => {
    try {
      await dispatch(removePriceRuleFromCategory(catId, priceRule.id));
      toast.success("Kategori kurala bağlılıktan kaldırıldı.");
    } catch (err) {
      toast.error("Kategori kaldırılırken bir hata oluştu.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              wordBreak: "break-word",
            }}
          >
            {priceRule?.title} - Kategori Atama
          </Typography>
          {isMobile && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Uyarı Notu */}
        <Box mb={2}>
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Kategori ataması yapmadan önce <b>pazaryeri ataması</b> yapınız.
            Eğer ilgili kategorinin pazaryeri ataması yapılmadı ise kural
            çalışmaz.
          </Alert>
        </Box>
        <Box mb={2}>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontWeight: "bold",
            }}
          >
            Kurala Bağlı Kategoriler
          </Typography>
          {categoriesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 1,
                maxHeight: { xs: "120px", sm: "auto" },
                overflowY: "auto",
              }}
            >
              {categoriesByRule && categoriesByRule.length > 0 ? (
                categoriesByRule.map((cat) => (
                  <Chip
                    key={cat.id}
                    label={cat.name}
                    color="primary"
                    onDelete={() => handleRemoveAssignedCategory(cat.id)}
                    sx={{
                      mr: 1,
                      mb: 1,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  />
                ))
              ) : (
                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  }}
                >
                  Bu kurala bağlı kategori yok.
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {/* Kategori Seç ve Ata arayüzü */}
        <Box mb={2}>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontWeight: "bold",
              mb: 1,
            }}
          >
            Kategori Seç ve Ata
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kategori Ara"
                value={searchTerm}
                onChange={handleSearchTermChange}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
          </Grid>
          <TableContainer
            component={Paper}
            sx={{
              mt: 2,
              maxHeight: { xs: "200px", sm: "300px" },
            }}
          >
            <Table stickyHeader size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  <TableCell>Kategori Adı</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  allCategories.map((cat) => (
                    <TableRow key={cat.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            !!selectedCategories.find((c) => c.id === cat.id)
                          }
                          onChange={() => handleCategoryCheck(cat)}
                          size={isMobile ? "small" : "medium"}
                        />
                      </TableCell>
                      <TableCell>{cat.name}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mt: 2,
              maxHeight: { xs: "100px", sm: "auto" },
              overflowY: "auto",
            }}
          >
            {selectedCategories.map((cat) => (
              <Chip
                key={cat.id}
                label={cat.name}
                onDelete={() => handleRemoveCategory(cat.id)}
                color="secondary"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
        <Button
          onClick={onClose}
          fullWidth={isMobile}
          sx={{ mb: { xs: 1, sm: 0 } }}
        >
          Kapat
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={selectedCategories.length === 0}
          fullWidth={isMobile}
        >
          Seçilen Kategorilere Ata
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CategoryAssignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  priceRule: PropTypes.object.isRequired,
};

export default CategoryAssignmentModal;
